import { writeClipboardText } from '@/libs/clipboard';
import type { ButtonProps, ClipboardRootProps, InputProps } from '@chakra-ui/react';
import { Button, Clipboard as ChakraClipboard, IconButton, Input } from '@chakra-ui/react';
import { Check, Link, PasteClipboard } from 'iconoir-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toaster } from './toaster';

interface SafeClipboardContextValue {
  copied: boolean;
  copy: () => Promise<boolean>;
}

const SafeClipboardContext = React.createContext<SafeClipboardContextValue | null>(null);

function useSafeClipboard(): SafeClipboardContextValue {
  const context = React.useContext(SafeClipboardContext);
  if (!context) {
    throw new Error('Clipboard controls must be rendered inside ClipboardRoot');
  }
  return context;
}

export const ClipboardRoot = React.forwardRef<HTMLDivElement, ClipboardRootProps>(
  function ClipboardRoot({ value, children, ...props }, ref) {
    const { t } = useTranslation();
    const [copied, setCopied] = React.useState(false);
    const resetTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const currentValue = React.useRef(value ?? '');
    const copyAttempt = React.useRef(0);

    React.useEffect(() => {
      currentValue.current = value ?? '';
      copyAttempt.current += 1;
      setCopied(false);
      if (resetTimer.current) clearTimeout(resetTimer.current);
    }, [value]);

    React.useEffect(
      () => () => {
        if (resetTimer.current) clearTimeout(resetTimer.current);
      },
      []
    );

    const copy = React.useCallback(async () => {
      const copiedValue = value ?? '';
      const attempt = copyAttempt.current + 1;
      copyAttempt.current = attempt;
      if (!(await writeClipboardText(copiedValue))) {
        if (copyAttempt.current !== attempt || currentValue.current !== copiedValue) return false;
        toaster.error({ title: t('common.copyFailed') });
        return false;
      }

      if (copyAttempt.current !== attempt || currentValue.current !== copiedValue) return true;
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 1500);
      return true;
    }, [t, value]);

    const context = React.useMemo(() => ({ copied, copy }), [copied, copy]);

    return (
      <SafeClipboardContext.Provider value={context}>
        <ChakraClipboard.Root ref={ref} value={value} {...props}>
          {children}
        </ChakraClipboard.Root>
      </SafeClipboardContext.Provider>
    );
  }
);

interface ClipboardIndicatorProps {
  children: React.ReactNode;
  copied: React.ReactNode;
}

export const ClipboardIndicator: React.FC<ClipboardIndicatorProps> = ({ children, copied }) => {
  const clipboard = useSafeClipboard();
  return clipboard.copied ? copied : children;
};

const ClipboardIcon = () => (
  <ClipboardIndicator copied={<Check />}>
    <PasteClipboard />
  </ClipboardIndicator>
);

const ClipboardCopyText = () => {
  const { copied } = useSafeClipboard();
  const { t } = useTranslation();
  return copied ? t('common.copied') : t('common.copy');
};

function useCopyClick(onClick: ButtonProps['onClick']): ButtonProps['onClick'] {
  const { copy } = useSafeClipboard();

  return React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) await copy();
    },
    [copy, onClick]
  );
}

export const ClipboardLabel = React.forwardRef<HTMLLabelElement, ChakraClipboard.LabelProps>(
  function ClipboardLabel(props, ref) {
    return (
      <ChakraClipboard.Label
        textStyle="sm"
        fontWeight="medium"
        display="inline-block"
        mb="1"
        {...props}
        ref={ref}
      />
    );
  }
);

export const ClipboardButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function ClipboardButton({ children, onClick, ...props }, ref) {
    const handleClick = useCopyClick(onClick);
    return (
      <Button ref={ref} size="sm" variant="surface" onClick={handleClick} {...props}>
        {children ?? (
          <>
            <ClipboardIcon />
            <ClipboardCopyText />
          </>
        )}
      </Button>
    );
  }
);

export const SafeClipboardTrigger = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function SafeClipboardTrigger({ children, onClick, ...props }, ref) {
    const handleClick = useCopyClick(onClick);
    return (
      <Button ref={ref} unstyled asChild onClick={handleClick} {...props}>
        {children}
      </Button>
    );
  }
);

export const ClipboardLink = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function ClipboardLink({ children, onClick, ...props }, ref) {
    const handleClick = useCopyClick(onClick);
    return (
      <Button
        unstyled
        variant="plain"
        size="xs"
        display="inline-flex"
        alignItems="center"
        gap="2"
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {children ?? (
          <>
            <Link />
            <ClipboardCopyText />
          </>
        )}
      </Button>
    );
  }
);

interface ClipboardIconButtonProps extends ButtonProps {
  copyLabel?: string;
  copiedLabel?: string;
}

export const ClipboardIconButton = React.forwardRef<HTMLButtonElement, ClipboardIconButtonProps>(
  function ClipboardIconButton({ children, onClick, copyLabel, copiedLabel, ...props }, ref) {
    const handleClick = useCopyClick(onClick);
    const { copied } = useSafeClipboard();
    const { t } = useTranslation();
    return (
      <IconButton
        ref={ref}
        size="xs"
        variant="subtle"
        onClick={handleClick}
        aria-label={copied ? (copiedLabel ?? t('common.copied')) : (copyLabel ?? t('common.copy'))}
        {...props}
      >
        {children ?? <ClipboardIcon />}
      </IconButton>
    );
  }
);

export const ClipboardInput = React.forwardRef<HTMLInputElement, InputProps>(
  function ClipboardInputElement(props, ref) {
    return (
      <ChakraClipboard.Input asChild>
        <Input ref={ref} {...props} />
      </ChakraClipboard.Input>
    );
  }
);
